import * as React from "react";
import {
  AvailabilityInput,
  useDebouncedAvailabilityCheck,
  type AvailabilityStatus,
} from "@aisquare/ui";
import { Section, VariantBlock } from "../lib/Section";

/**
 * A fake availability transport, injected so each block can reach a terminal
 * state without a real network. Mirrors the live `StudioUrlCheck` shape
 * (`{ available, suggested? }`) and resolves after a tick so the spinner is
 * actually visible. Values in `taken` come back as unavailable with a
 * suggestion; pass `fail` to drive the error glyph.
 */
function makeCheck(opts: {
  taken?: Set<string>;
  suggest?: (v: string) => string;
  fail?: boolean;
  latency?: number;
} = {}) {
  return (value: string) =>
    new Promise<{ available: boolean; suggested?: string }>((resolve, reject) => {
      setTimeout(() => {
        if (opts.fail) {
          reject(new Error("availability service unreachable"));
          return;
        }
        const taken = opts.taken?.has(value) ?? false;
        resolve({
          available: !taken,
          suggested: taken ? opts.suggest?.(value) ?? `${value}-2` : undefined,
        });
      }, opts.latency ?? 450);
    });
}

// A shared transport for the interactive field: "studio" and "admin" are taken
// and offer a suggestion; everything else is free.
const interactiveCheck = makeCheck({
  taken: new Set(["studio", "admin", "aurora"]),
  suggest: (v) => `${v}-hq`,
});

/** Controlled field so a single source of truth drives value + onChange. */
function ControlledField({
  initial,
  ...props
}: { initial?: string } & Omit<
  React.ComponentProps<typeof AvailabilityInput>,
  "value" | "onChange"
>) {
  const [value, setValue] = React.useState(initial ?? "");
  return <AvailabilityInput value={value} onChange={setValue} {...props} />;
}

/** Tone token per status so the live read-out matches the field's glyph. */
const STATUS_TONE: Record<AvailabilityStatus, string> = {
  idle: "text-muted-foreground",
  checking: "text-muted-foreground",
  available: "text-success",
  taken: "text-destructive",
  error: "text-warning",
};

/**
 * Hook-only widget: calls `useDebouncedAvailabilityCheck` directly (no
 * `AvailabilityInput` wrapper) and renders its raw state so the debounce +
 * out-of-order guard + tri-state machine are observable on their own. Type to
 * watch idle -> checking -> available / taken, with `available` resting at
 * `null` until the first resolution.
 */
function HookProbe() {
  const [value, setValue] = React.useState("studio");
  const result = useDebouncedAvailabilityCheck({
    value,
    delay: 400,
    enabled: value.length > 0,
    check: interactiveCheck,
    getAvailability: (res) => ({
      available: res.available,
      suggestion: res.suggested,
    }),
  });

  return (
    <div className="grid w-80 gap-3">
      <div className="grid gap-1.5">
        <label htmlFor="hook-probe" className="text-xs font-medium text-foreground">
          Raw hook input
        </label>
        <input
          id="hook-probe"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="try studio, admin, or anything free"
          className="flex h-10 w-full rounded-lg border border-input bg-background px-3 text-sm text-foreground outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
        />
      </div>

      <dl className="grid grid-cols-[6rem_1fr] gap-x-3 gap-y-1.5 rounded-lg border border-border bg-muted/30 p-3 text-xs">
        <dt className="text-muted-foreground">status</dt>
        <dd className={`font-mono font-medium ${STATUS_TONE[result.status]}`}>
          {result.status}
        </dd>

        <dt className="text-muted-foreground">available</dt>
        <dd className="font-mono text-foreground">
          {result.available === null ? "null" : String(result.available)}
        </dd>

        <dt className="text-muted-foreground">suggestion</dt>
        <dd className="font-mono text-foreground">{result.suggestion ?? "—"}</dd>
      </dl>
    </div>
  );
}

export function AvailabilityInputDemo() {
  return (
    <Section
      id="availability-input"
      title="AvailabilityInput"
      description="Debounced slug / name field with a live status glyph and one-tap apply-this-suggestion. A thin shell over the brand Input plus useDebouncedAvailabilityCheck (debounce + AbortController out-of-order guard + idle / checking / available / taken / error machine). Consolidates the studio custom-URL, workspace-name, and account-username fields that each hand-rolled the same wiring; the transport is always injected, never service-imported."
    >
      <VariantBlock label="Available" caption="a free value resolves to the check glyph + hint">
        <div className="w-80">
          <ControlledField
            initial="my-studio"
            aria-label="Custom URL"
            suffix=".aisquare.studio"
            checkAvailability={makeCheck()}
          />
        </div>
      </VariantBlock>

      <VariantBlock label="Interactive" caption="type studio, admin, or aurora to see the taken path">
        <div className="w-80">
          <ControlledField
            initial=""
            aria-label="Custom URL"
            placeholder="your-studio"
            suffix=".aisquare.studio"
            checkAvailability={interactiveCheck}
          />
        </div>
      </VariantBlock>

      <VariantBlock label="Taken (inline-link suggestion)" caption="click the suggested value to apply it">
        <div className="w-80">
          <ControlledField
            initial="studio"
            aria-label="Custom URL"
            suffix=".aisquare.studio"
            checkAvailability={makeCheck({
              taken: new Set(["studio"]),
              suggest: () => "studio-hq",
            })}
          />
        </div>
      </VariantBlock>

      <VariantBlock label="Taken (chips)" caption="same state rendered as fe-react-style suggestion chips">
        <div className="w-80">
          <ControlledField
            initial="studio"
            aria-label="Custom URL"
            suggestionStyle="chips"
            suffix=".aisquare.studio"
            checkAvailability={makeCheck({
              taken: new Set(["studio"]),
              suggest: () => "studio-ai",
            })}
          />
        </div>
      </VariantBlock>

      <VariantBlock label="Error policies" caption="default blocks with the warn glyph; onErrorTreatAs='available' never blocks a flaky check">
        <div className="grid w-80 gap-4">
          <ControlledField
            initial="anything"
            aria-label="Custom URL (blocks on error)"
            checkAvailability={makeCheck({ fail: true })}
          />
          <ControlledField
            initial="anything"
            aria-label="Custom URL (passes on error)"
            onErrorTreatAs="available"
            checkAvailability={makeCheck({ fail: true })}
          />
        </div>
      </VariantBlock>

      <VariantBlock label="Nudge + local validation" caption="short-but-free draws a soft hint; a sync validate rejects before the network is hit">
        <div className="grid w-80 gap-4">
          <ControlledField
            initial="ab"
            aria-label="Short name"
            suffix=".aisquare.studio"
            checkAvailability={makeCheck()}
          />
          <ControlledField
            initial="bad-"
            aria-label="Validated URL"
            suffix=".aisquare.studio"
            validate={(v) => (v.endsWith("-") ? "URL cannot end with a hyphen." : null)}
            checkAvailability={makeCheck()}
          />
        </div>
      </VariantBlock>

      <VariantBlock label="Username (prefix, no frame suffix)" caption="the account-handle field; @ prefix, reserved handles are taken">
        <div className="w-80">
          <ControlledField
            initial="ada"
            aria-label="Username"
            prefix="@"
            checkAvailability={makeCheck({ taken: new Set(["admin", "root"]) })}
          />
        </div>
      </VariantBlock>

      <VariantBlock label="useDebouncedAvailabilityCheck (hook)" caption="the bare hook driving its own read-out — watch status + available transition">
        <HookProbe />
      </VariantBlock>
    </Section>
  );
}
